import styled, { css } from 'styled-components';
import { SIDEBAR_MOBILE_BREAKPOINT } from '@constants/sidebar';

export const Description = styled.p<{ $compact?: boolean }>`
  max-width: 640px;
  line-height: 1.5;

  ${({ theme: { colors, font, spacing }, $compact }) => css`
    margin: 0 0 ${$compact ? spacing.level3 : spacing.level5} 0;
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 800px;

  ${({ theme: { spacing } }) => css`
    margin: ${spacing.level5} 0 ${spacing.level3};
  `}
`;

export const SectionTitle = styled.h2`
  margin: 0;

  ${({ theme: { colors, font } }) => css`
    font-size: ${font.titleMedium.fontSize};
    font-weight: ${font.titleMedium.fontWeight || 600};
    color: ${colors.onSurface};
  `}
`;

export const SkillList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  max-width: 800px;
  margin: 0;
  padding: 0;
  gap: ${({ theme }) => theme.spacing.level3};
`;

export const SkillCard = styled.li<{ $off?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  ${({ theme: { colors, spacing, borderRadius }, $off }) => css`
    background: ${colors.surfaceContainer};
    border-radius: ${borderRadius.large};
    padding: ${spacing.level4} ${spacing.level5};
    gap: ${spacing.level4};
    /* Dimmed, not hidden: a switched-off skill stays where the user left it. */
    opacity: ${$off ? 0.55 : 1};
  `}

  @media (max-width: ${SIDEBAR_MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SkillInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: ${({ theme }) => theme.spacing.level1};
`;

export const SkillNameRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.level2};
`;

export const SkillName = styled.span`
  ${({ theme: { colors, font } }) => css`
    font-size: ${font.bodyLarge.fontSize};
    font-weight: 600;
    color: ${colors.onSurface};
  `}
`;

export const SkillId = styled.span`
  ${({ theme: { colors, font } }) => css`
    font-size: ${font.bodySmall.fontSize};
    font-family: ${font.bodySmall.fontFamily || 'inherit'};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const BuiltInBadge = styled.span`
  ${({ theme: { colors, font, spacing, borderRadius } }) => css`
    font-size: ${font.labelSmall.fontSize};
    color: ${colors.onSurfaceVariant};
    background: ${colors.surfaceContainerHighest};
    border-radius: ${borderRadius.small};
    padding: 2px ${spacing.level2};
  `}
`;

export const SkillDescription = styled.p`
  margin: 0;
  line-height: 1.5;

  ${({ theme: { colors, font } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const SkillTools = styled.span`
  ${({ theme: { colors, font } }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.onSurfaceVariant};
    opacity: 0.8;
  `}
`;

export const SkillActions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level2};

  @media (max-width: ${SIDEBAR_MOBILE_BREAKPOINT}px) {
    justify-content: flex-end;
  }
`;

export const StaleBanner = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 800px;

  ${({ theme: { colors, spacing, borderRadius } }) => css`
    background: ${colors.surfaceContainerHighest};
    border-radius: ${borderRadius.large};
    padding: ${spacing.level4} ${spacing.level5};
    margin-top: ${spacing.level5};
    gap: ${spacing.level3};
  `}
`;

export const StaleBannerText = styled.p`
  margin: 0;
  line-height: 1.5;

  ${({ theme: { colors, font } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const StaleList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  gap: ${({ theme }) => theme.spacing.level2};
`;

export const StaleRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.level3};
`;

export const StaleSkillId = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ theme: { colors, font } }) => css`
    font-size: ${font.bodySmall.fontSize};
    font-family: ${font.bodySmall.fontFamily || 'inherit'};
    color: ${colors.onSurface};
  `}
`;

export const EmptyState = styled.div`
  max-width: 800px;

  ${({ theme: { colors, font, spacing, borderRadius } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurfaceVariant};
    background: ${colors.surfaceContainer};
    border-radius: ${borderRadius.large};
    padding: ${spacing.level6};
    text-align: center;
  `}
`;

export const StateContainer = styled.div<{ $error?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.level8} 0;

  ${({ theme: { colors, font }, $error }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${$error ? colors.error : colors.onSurfaceVariant};
  `}
`;

export const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.level4};
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.level1};
`;

export const FieldLabel = styled.label`
  ${({ theme: { colors, font } }) => css`
    font-size: ${font.labelMedium.fontSize};
    font-weight: 600;
    color: ${colors.onSurfaceVariant};
  `}
`;

export const FieldHint = styled.span<{ $warning?: boolean }>`
  ${({ theme: { colors, font }, $warning }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${$warning ? colors.error : colors.onSurfaceVariant};
    opacity: ${$warning ? 1 : 0.7};
  `}
`;

const fieldStyles = css`
  width: 100%;
  border: none;
  outline: none;

  ${({ theme: { colors, font, spacing, borderRadius } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    font-family: ${font.bodyMedium.fontFamily || 'inherit'};
    color: ${colors.onSurface};
    background: ${colors.surfaceContainerHighest};
    border-radius: ${borderRadius.small};
    padding: ${spacing.level3};

    &::placeholder {
      color: ${colors.onSurfaceVariant};
      opacity: 0.6;
    }

    &:focus {
      box-shadow: inset 0 0 0 1px ${colors.primary};
    }

    &:disabled {
      opacity: 0.7;
    }
  `}
`;

export const FieldInput = styled.input`
  ${fieldStyles}
`;

export const FieldTextArea = styled.textarea`
  ${fieldStyles}
  min-height: 180px;
  resize: vertical;
  line-height: 1.6;
`;

export const FormError = styled.p`
  margin: 0;
  line-height: 1.4;

  ${({ theme: { colors, font } }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.error};
  `}
`;

export const FileList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.level1};
`;

export const FileRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.level2};

  ${({ theme: { colors, spacing, borderRadius } }) => css`
    background: ${colors.surfaceContainerHighest};
    border-radius: ${borderRadius.small};
    padding: ${spacing.level2} ${spacing.level3};
  `}
`;

export const FileMeta = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ theme: { colors, font } }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.onSurface};
  `}
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const ToolGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  max-height: min(320px, 40vh);
  overflow-y: auto;
  overscroll-behavior: contain;

  ${({ theme: { colors, spacing, borderRadius } }) => css`
    gap: 0 ${spacing.level4};
    background: ${colors.surfaceContainerHighest};
    border-radius: ${borderRadius.small};
    padding: ${spacing.level3} ${spacing.level4};
  `}
`;

export const ToolOption = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: ${({ theme }) => theme.spacing.level2};
`;

export const ToolOptionLabel = styled.label`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  ${({ theme: { colors, font } }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.onSurface};
  `}
`;

export const ToolBadge = styled.span`
  flex-shrink: 0;
  white-space: nowrap;

  ${({ theme: { colors, font, spacing, borderRadius } }) => css`
    font-size: ${font.labelSmall.fontSize};
    color: ${colors.onSurfaceVariant};
    background: ${colors.surfaceContainerHigh};
    border-radius: ${borderRadius.small};
    padding: 0 ${spacing.level2};
  `}
`;

export const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level2};
`;

export const CheckboxLabel = styled.label`
  cursor: pointer;

  ${({ theme: { colors, font } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurface};
  `}
`;

export const DetailLabel = styled.span`
  ${({ theme: { colors, font } }) => css`
    font-size: ${font.labelMedium.fontSize};
    font-weight: 600;
    color: ${colors.onSurfaceVariant};
  `}
`;

export const GuidanceBlock = styled.pre`
  max-height: 45vh;
  overflow: auto;
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: 1.6;

  ${({ theme: { colors, font, spacing, borderRadius } }) => css`
    font-size: ${font.bodySmall.fontSize};
    font-family: ${font.bodySmall.fontFamily || 'inherit'};
    color: ${colors.onSurface};
    background: ${colors.surfaceContainerHighest};
    border-radius: ${borderRadius.small};
    padding: ${spacing.level3};
  `}
`;
